import * as providerConfigs from './configs';
import type { ProviderConfig } from './configs';
import { useSecondLookIntegration } from '../../queries';

/**
 * Safely looks up a second-look provider's base config by its tenant-setting name (e.g. "Turns",
 * "Finturf").
 *
 * Uses a direct lookup against the registered provider configs rather than going through
 * `useProviderConfig`, which throws for unrecognised providers and which resolves flow-specific
 * variants (e.g. `finturfTechnician`) we don't need here. A future provider that appears in
 * tenant settings before a `ProviderConfig` entry exists will degrade gracefully: callers get
 * `undefined` back instead of a crash.
 */
const getSecondLookProviderConfig = (providerName: string): ProviderConfig | undefined => {
    const matchingKey = (Object.keys(providerConfigs) as (keyof typeof providerConfigs)[]).find(
        key => key.toLowerCase() === providerName.toLowerCase(),
    );
    return matchingKey ? providerConfigs[matchingKey] : undefined;
};

/**
 * Resolves the customer-facing display name for the tenant's configured second-look provider.
 *
 * The name is read from the resolved second-look provider's {@link ProviderConfig.providerDisplayName},
 * falling back to the raw provider name and finally an empty string. Use this for any second-look
 * copy (declined screens, service-degraded alerts, CTAs) so the shared code path stays
 * provider-agnostic.
 */
export const useSecondLookProviderName = (): string => {
    const { secondLookProvider } = useSecondLookIntegration();
    if (!secondLookProvider) {
        return '';
    }
    return (
        getSecondLookProviderConfig(secondLookProvider)?.providerDisplayName ?? secondLookProvider
    );
};

/**
 * Resolves the dialog heading for the tenant's configured second-look provider (e.g. "Turns
 * Program Pre-Qualification Request"), read from that provider's
 * {@link ProviderConfig.preQualificationHeading}.
 *
 * Use this once the applicant has moved on to the second-look provider's offers (e.g. the offer
 * confirmation / application-sent screens), so the dialog header reflects that provider instead
 * of whichever first-look provider originally declined them. Returns `undefined` when no
 * second-look provider is configured or it has no heading defined, so callers can fall back to
 * their normal heading logic.
 */
export const useSecondLookPreQualificationHeading = (): string | undefined => {
    const { secondLookProvider } = useSecondLookIntegration();
    if (!secondLookProvider) {
        return undefined;
    }
    return getSecondLookProviderConfig(secondLookProvider)?.preQualificationHeading;
};
